import React, { useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { ArchiveContext } from "../App.js";
import SEOMeta from "../components/SEOMeta.js";

const YearsPage: React.FC = () => {
  const { decadeName } = useParams<{ decadeName: string }>();
  const decodedDecade = decodeURIComponent(decadeName || "");
  const { yearCounts, isLoading, getYearCounts } = useContext(ArchiveContext);

  useEffect(() => {
    getYearCounts(decodedDecade);
  }, [decodedDecade, getYearCounts]);

  const uniqueYears = Object.keys(yearCounts)
    .map(Number)
    .sort((a, b) => a - b);

  if (isLoading && uniqueYears.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-6">
        <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 italic">
          Querying Year Nodes...
        </p>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${decodedDecade} Cyber Security Timeline | Year-by-Year Analysis`}
        description={`Complete year-by-year breakdown of cyber security events during ${decodedDecade}. Historical attacks, technological evolution, and security milestones.`}
        keywords={`${decodedDecade} cybersecurity, ${decodedDecade} timeline, historical cyber attacks ${decodedDecade}, year analysis cybersecurity, ${decodedDecade} security evolution`}
        
      />
      <div className="space-y-12 py-8 animate-in slide-in-from-right-4 duration-500 max-w-5xl mx-auto px-4">
        <nav className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 overflow-x-auto no-scrollbar whitespace-nowrap">
          <Link to="/decades" className="hover:text-blue-500 transition-colors">
            Global Archive
          </Link>
          <i className="fa-solid fa-chevron-right text-[8px] opacity-30"></i>
          <span className="text-white">{decodedDecade} ERA</span>
        </nav>

        <div className="space-y-4">
          <h1 className="text-6xl font-black italic tracking-tighter uppercase leading-none text-white">
            {decodedDecade}
          </h1>
          <p className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-500">
            Chronological Distribution for {decodedDecade}. Select a node to
            view incident records.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {uniqueYears.map((year) => (
            <Link
              key={year}
              to={`/year/${year}`}
              className="p-8 bg-white/5 border border-white/5 rounded-[2rem] hover:border-blue-500 hover:bg-white/10 transition-all text-center group shadow-xl relative"
            >
              <span className="text-3xl font-black block mb-2 group-hover:text-blue-400 transition-colors italic">
                {year < 0 ? `${Math.abs(year)}` : year}
              </span>
              <span className="text-[9px] uppercase font-black text-slate-500 tracking-widest group-hover:text-slate-300">
                {yearCounts[year]} INCIDENTS
              </span>
              {yearCounts[year] > 0 && (
                <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
              )}
            </Link>
          ))}
        </div>

        {uniqueYears.length === 0 && !isLoading && (
          <div className="p-32 text-center border-4 border-dashed border-white/5 rounded-[4rem] bg-white/5">
            <p className="text-slate-500 font-black uppercase tracking-widest text-sm">
              No records committed to this era yet.
            </p>
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mt-4 italic">
              Synchronization Status: Null
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default YearsPage;
